import type { WriteRejection } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RejectionNotice } from "@/modules/docs/screens/RejectionNotice";

/**
 * The one thing every "New document", "New folder" and "Rename" action in
 * this module asks for: a name (FR-DOC-1, FR-DOC-2, FR-DOC-10's rename half).
 *
 * A small `Dialog` rather than the tree growing an inline text row per the
 * ticket's own "inline or in a small dialog" — a dialog needs no per-row
 * state machine in `FolderTree`, which stays the one shared tree component
 * `Browse` and `Permissions` both render without either owning a "which row
 * is being renamed right now" concern.
 *
 * Fully controlled (`value`/`onChangeValue`) rather than holding its own
 * draft: the same dialog serves a blank "New folder" and a "Rename" prefilled
 * with the current name, and a caller-owned value is what lets the caller
 * seed that prefill at the moment it opens the dialog, in the same click
 * handler that decides which folder is being renamed — an internal draft
 * synced from an `initialValue` prop would need an effect to notice a second
 * rename opening before the first one's value had been cleared.
 */
export function NamePromptDialog({
  open,
  onOpenChange,
  title,
  label,
  placeholder,
  hint,
  value,
  onChangeValue,
  submitLabel = "Create",
  pending,
  rejection,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  placeholder?: string;
  /** Where this will land — "Inside /handbook/warehouse", say. */
  hint?: string;
  value: string;
  onChangeValue: (value: string) => void;
  submitLabel?: string;
  pending: boolean;
  rejection: WriteRejection | null;
  onSubmit: (name: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={title}>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = value.trim();
            if (trimmed === "") return;
            onSubmit(trimmed);
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="docs-name-prompt">{label}</Label>
            <Input
              id="docs-name-prompt"
              autoFocus
              value={value}
              onChange={(event) => {
                onChangeValue(event.target.value);
              }}
              placeholder={placeholder}
            />
            {hint ? (
              <p className="text-muted-foreground text-xs">{hint}</p>
            ) : null}
          </div>

          {rejection ? <RejectionNotice rejection={rejection} /> : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={pending || value.trim() === ""}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
